import React, { useRef, useEffect, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllCndnTypes } from "http-hooks/cndntypes/useCndnTypes";

const SelectCndnTypes = ({
  value,
  onChange,
  placeholder = "Search credit/debit note types...",
  isDisabled = false,
  isRequired = false,
  label = "Credit/Debit Note Type",
  className = "",
  filters = {},
  showLabel = true,
  isMulti = false,
  initialValue = null,
  showAddButton = true, // New prop to control plus button visibility
}) => {
  const hasInitialized = useRef(false);
  const { openModal } = useModalStore();
  
  const {
    isLoading: isLoadingAllCndnTypes,
    data: { data: allCndnTypes } = [],
  } = useAllCndnTypes({
    name: "",
    ag_status: "1", // Only active types by default
    noPagination: true,
    ...filters,
  });

  const DIRECT_WITHOUT_ITEM_OPTION = useMemo(() => ({
    value: "__direct__",
    label: "Direct (Without Item)",
    name: "Direct(Without Item)",
  }), []);

  const cndnTypesOptions = useMemo(() => {
    const masterOptions = allCndnTypes?.map(cndnType => ({
      value: cndnType.id,
      label: cndnType.name,
      name: cndnType.name,
      ...cndnType
    })) || [];
    return [DIRECT_WITHOUT_ITEM_OPTION, ...masterOptions];
  }, [allCndnTypes, DIRECT_WITHOUT_ITEM_OPTION]);

  // Handle initial value selection
  useEffect(() => {
    if (initialValue && !hasInitialized.current && cndnTypesOptions.length > 0 && onChange) {
      try {
        if (!initialValue.name) {
          throw new Error('initialValue must contain name field');
        }
        
        const matchingOption = cndnTypesOptions.find(option => 
          option.name === initialValue.name
        );
        
        if (matchingOption) {
          onChange(isMulti ? [matchingOption] : matchingOption);
          hasInitialized.current = true;
        }
      } catch (error) {
        console.error('SelectCndnTypes initialValue error:', error);
        throw error;
      }
    }
  }, [initialValue, cndnTypesOptions, onChange, isMulti]);

  const handleAddNew = () => {
    openModal('cndntypes');
  };

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new credit/debit note type"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={cndnTypesOptions}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingAllCndnTypes}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
        isMulti={isMulti}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new credit/debit note type"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectCndnTypes;
